'use client';

import { intersection, startCase } from 'lodash';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import {
  Button,
  Container,
  Heading,
  Icon,
  IconSvg,
  Input,
  Link,
  Placeholder,
  PromoCard,
  useData,
} from '@/components';
import { useSpecialsContext } from '@/context';
import {
  PromoCarouselDocumentDataCarouselItemsItem,
  PromoCarouselDocumentDataCarouselItemsItemWithID,
} from '@/types';
import {
  ChangeEvent,
  Dispatch,
  FC,
  Ref,
  SetStateAction,
  useState,
} from 'react';
import {
  getActivesPreCartDiscounts,
  getPreCartTreezDiscounts,
  getQueryByText,
  getSpecialURL,
} from '@/utils';
import { useSelectedStore } from '@/hooks';
import { useUiStore } from '@/data';
import { TEMPLATE_BASE_PATH } from '@/constants';
import classNames from 'classnames';
import { usePopper } from 'react-popper';

type TFilters = {
  brands: string[];
  categories: string[];
};
interface IPromotionFilterProps {
  filters: TFilters;
  icon?: 'leaf';
  options: string[];
  setFilters: Dispatch<SetStateAction<TFilters>>;
  type: 'brands' | 'categories';
}

const PromotionsGrid: FC = () => {
  const { promoCarousel } = useData();
  const { specials, isLoading } = useSpecialsContext();

  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<TFilters>({
    brands: [],
    categories: [],
  });

  const preCartTreezDiscounts = getPreCartTreezDiscounts(specials);

  const cmsDiscountsItems = promoCarousel?.carousel_items;

  const activesPreCartDiscounts = getActivesPreCartDiscounts(
    cmsDiscountsItems as PromoCarouselDocumentDataCarouselItemsItem[],
    preCartTreezDiscounts,
  );

  const activesPreCartDiscountsValues = Object.values(
    activesPreCartDiscounts,
  ) as PromoCarouselDocumentDataCarouselItemsItemWithID[];

  const filteredSpecials = activesPreCartDiscountsValues?.filter(special => {
    const queryByText = getQueryByText(special, searchTerm);

    //get intersections between the specials arrangements and the applied filters
    const brandsIntersect = intersection(special?.brands, filters?.brands);
    const categoriesIntersect = intersection(
      special?.categories,
      filters?.categories,
    );

    //evaluate that there are filters applied
    const hasBrands = !!filters?.brands?.length;
    const hasCategories = !!filters?.categories?.length;

    //evaluate that the filters applied are decisive for the current special
    const hasBrandsIntersect = hasBrands && !!brandsIntersect?.length;
    const hasCategoriesIntersect =
      hasCategories && !!categoriesIntersect?.length;

    //evaluate that there is no filter applied
    const query1 = !hasBrands && !hasCategories;

    //get all applied filters determine to special
    const query2 = hasBrandsIntersect && hasCategoriesIntersect;

    //get that the brands filters determine the special and do not have categories filters applied
    const query3 = hasBrandsIntersect && !hasCategories;

    //get that the categories filters determine the special and do not have brands filters applied
    const query4 = hasCategoriesIntersect && !hasBrands;

    const queryByFilters = query1 || query2 || query3 || query4;

    return queryByText && queryByFilters;
  });

  let brandsOptions: Set<string> | string[] = new Set<string>();
  let categoriesOptions: Set<string> | string[] = new Set<string>();

  activesPreCartDiscountsValues.forEach(special => {
    special?.brands?.forEach(v => v && (brandsOptions as Set<string>).add(v));
    special?.categories?.forEach(
      v => v && (categoriesOptions as Set<string>).add(v),
    );
  });

  brandsOptions = Array.from(brandsOptions);
  categoriesOptions = Array.from(categoriesOptions);

  return (
    <Container>
      <div className="promotion__filters">
        <div className="promotion__dropdowns">
          {!!brandsOptions?.length && (
            <PromotionFilter
              filters={filters}
              options={brandsOptions}
              setFilters={setFilters}
              type="brands"
            />
          )}
          {!!categoriesOptions?.length && (
            <PromotionFilter
              filters={filters}
              icon="leaf"
              options={categoriesOptions}
              setFilters={setFilters}
              type="categories"
            />
          )}
        </div>
        <div className="promotion__search_box">
          <Input
            autoComplete="off"
            className="promotion__search_box__input"
            disabled={isLoading}
            isSmall
            name={'searchBox'}
            onChange={e => {
              setSearchTerm(e.target.value);
            }}
            placeholder="Search"
            type="search"
            value={searchTerm}
          />
          <Button
            ariaLabel="Search"
            className="promotion__search_box__button"
            color="primary"
            onClick={() => {}}
          >
            <IconSvg name="search" />
          </Button>
        </div>
      </div>

      {isLoading && <SpecialsSkeleton />}
      {!isLoading && (
        <>
          {!filteredSpecials?.length && <SpecialsNotFound />}
          {filteredSpecials?.length > 0 && (
            <div className="promotions__container">
              {filteredSpecials?.map((special, index) => (
                <PromoCard
                  className="promotion"
                  key={index}
                  special={special}
                  specialUrl={getSpecialURL(special)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </Container>
  );
};

const PromotionFilter: FC<IPromotionFilterProps> = ({
  filters,
  icon,
  options,
  setFilters,
  type,
}) => {
  const [button, setButton] = useState<HTMLButtonElement>();
  const [panel, setPanel] = useState<HTMLDivElement>();

  const { styles: popperStyles, attributes } = usePopper(button, panel, {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 10],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          padding: 16,
        },
      },
    ],
    placement: 'bottom-start',
  });

  return (
    <Popover className={'dropdown'}>
      {({ open }) => (
        <>
          <PopoverButton
            aria-label="Show info"
            className={classNames('dropdown__button', {
              ['dropdown__button_active']: filters?.[type]?.length,
              ['dropdown__button_open']: open,
            })}
            ref={setButton as Ref<HTMLButtonElement>}
          >
            <div className="dropdown__button_label">
              {!!icon && <IconSvg name={icon} />}
              {startCase(type)}
              {!!filters?.[type]?.length && (
                <span className="count">{`${filters?.[type]?.length}`}</span>
              )}
            </div>
            <IconSvg name="chevron-right" />
          </PopoverButton>
          <PopoverPanel
            className="dropdown__panel"
            ref={setPanel as Ref<HTMLDivElement>}
            style={popperStyles.popper}
            {...attributes.popper}
          >
            <div className="dropdown__panel_list">
              {options.map((option, index) => (
                <Input
                  checked={filters?.[type]?.includes(option)}
                  key={index}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    const newFilters = new Set(filters?.[type]);

                    if (e?.target?.checked) {
                      newFilters.add(option);
                    } else {
                      newFilters.delete(option);
                    }

                    setFilters({
                      ...filters,
                      [type]: Array.from(newFilters),
                    });
                  }}
                  type="checkbox"
                >
                  {option}
                </Input>
              ))}
            </div>
            <div className="button_row">
              <Button
                ariaLabel="Clear"
                className="panel_button"
                color="secondary"
                onClick={() => setFilters({ ...filters, [type]: [] })}
              >
                <IconSvg name="bin" />
                <span>Clear</span>
              </Button>
            </div>
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
};

const SpecialsSkeleton = () => {
  return (
    <div className="promotions__container">
      {Array.from({ length: 10 }).map((_, index) => {
        return (
          <Placeholder className="promotions__skeleton_item" key={index} />
        );
      })}
    </div>
  );
};

const SpecialsNotFound = () => {
  const { store } = useSelectedStore();
  const [, setState] = useUiStore();

  return (
    <div className="promotion_empty">
      <Icon name="search" />
      <Heading level={5}>
        {store?.shortName
          ? 'Promotion not found.'
          : // eslint-disable-next-line max-len
            'Select your store to unlocked this bundle'}
      </Heading>

      {store?.shortName && (
        <div className="promotion_empty__actions">
          <p>Click </p>
          <Link className="action" href={TEMPLATE_BASE_PATH.SHOP}>
            here to visit the Shop Page.
          </Link>
        </div>
      )}
      {!store?.shortName && (
        <div className="promotion_empty__actions">
          Click{' '}
          <p
            className="action"
            onClick={() =>
              setState({
                is_store_location_open: true,
              })
            }
            role="button"
          >
            here select your store.
          </p>
        </div>
      )}
    </div>
  );
};

export { PromotionsGrid };
